'use strict';
/* ==========================================================================
   levels/title.js — the title screen, NOTEBOOK style. On the graph paper: a bike wheel drawn in ink by code stands on
   a strip of till-roll paper (the floor strip) with one wet-paint spot at the bottom. It rolls exactly one turn
   (the angle from Circle.angle, the spot from Circle.mark, so the drawing agrees with the model), stamping the strip at
   the start and again at π·d. Then three red-pen "across" sticks (one diameter each) lay themselves along the track and
   the leftover is highlighted: "3 and a bit". Jay rises in from below on the right. The title is handwritten with a
   highlighter swipe on "Round" (NB.title from lesson.json), ▶ Play → the map. Pure in time: everything is keyed to T
   and the scene's entry time. On a phone held upright the controls move to the portrait dock (Dock.title) and the
   stage keeps only the art. Levels.title.loading() draws the loading page while the boot tier loads.
   ========================================================================== */
(function () {
  const PLAY = { x: 1400, y: 820 };
  const HANDF = NB.FONT.hand, INK = NB.PAL.ink, RED = NB.PAL.redpen;
  const D = 160, R = D / 2;                          // the wheel's diameter in logical px (the model is unit-free)
  const X0 = 230, FLOOR = 800;                       // where the paint spot touches the floor at the start
  const TURN = Circle.turn(D), STICKS = Circle.sticks(D);

  /* the title in one or two lines, each sized to fit maxW logical px */
  function titleLines(text, maxW, max) {
    const width = (s, size) => { g.save(); g.font = `700 ${size}px ${HANDF}`; const w = g.measureText(s).width; g.restore(); return w; };
    const words = String(text).split(/\s+/).filter(Boolean);
    let lines = [words.join(' ')];
    if (width(lines[0], max) > maxW && words.length > 1) {
      let best = null;
      for (let k = 1; k < words.length; k++) {
        const a = words.slice(0, k).join(' '), b = words.slice(k).join(' '), d = Math.abs(width(a, 100) - width(b, 100));
        if (!best || d < best.d) best = { d, lines: [a, b] };
      }
      lines = best.lines;
    }
    const size = Math.min(max, ...lines.map(l => max * maxW / Math.max(1, width(l, max))));
    return { lines, size };
  }

  /* the wheel at hub (cx, cy) turned by th (clockwise on screen); the paint spot at local (0, r) */
  function wheel(cx, cy, th) {
    g.save(); g.translate(cx, cy); g.rotate(th);
    g.lineCap = 'round';
    g.strokeStyle = INK; g.lineWidth = 10; g.beginPath(); g.arc(0, 0, R - 5, 0, TAU); g.stroke();          // tyre
    g.lineWidth = 3; g.beginPath(); g.arc(0, 0, R - 16, 0, TAU); g.stroke();                               // rim
    g.lineWidth = 2;
    for (let k = 0; k < 16; k++) { const a = k * TAU / 16; g.beginPath(); g.moveTo(Math.cos(a) * 7, Math.sin(a) * 7); g.lineTo(Math.cos(a) * (R - 16), Math.sin(a) * (R - 16)); g.stroke(); }
    g.fillStyle = INK; g.beginPath(); g.arc(0, 0, 9, 0, TAU); g.fill();                                     // hub
    g.fillStyle = RED; g.beginPath(); g.arc(0, R - 5, 9, 0, TAU); g.fill();                                 // wet paint on the tyre
    g.restore();
  }

  /* the floor strip, the roll, the stamps, then the across sticks */
  function rollScene(t0) {
    // the till-roll strip
    g.save(); g.fillStyle = '#fffefa'; g.strokeStyle = NB.PAL.rule; g.lineWidth = 2;
    g.fillRect(X0 - 70, FLOOR, TURN + 200, 44); g.strokeRect(X0 - 70, FLOOR, TURN + 200, 44); g.restore();
    const s = TURN * E.inOutSine(P(T, t0 + 0.8, t0 + 3.0));
    const m = Circle.mark(D, s);
    // the paint stamps made so far (the model says where)
    for (const x of Circle.stamps(D, s + 1e-6)) {
      g.save(); g.fillStyle = RED; g.globalAlpha = 0.85; g.beginPath(); g.ellipse(X0 + x, FLOOR + 7, 13, 6, 0, 0, TAU); g.fill(); g.restore();
    }
    wheel(X0 + m.cx, FLOOR + m.cy, m.angle);
    // the across sticks: three diameters, then the bit
    const ts = t0 + 3.3, y = FLOOR + 74;
    for (let k = 0; k < STICKS.whole; k++) {
      const a = X0 + k * D, p = E.outCubic(P(T, ts + k * 0.35, ts + k * 0.35 + 0.3));
      if (p <= 0) continue;
      NB.pen([[a + 4, y], [a + 4 + (D - 8) * p, y]], { w: 6, color: RED });
      NB.pen([[a + 4, y - 12], [a + 4, y + 12]], { w: 4, color: RED });
      if (p >= 1) NB.pen([[a + D - 4, y - 12], [a + D - 4, y + 12]], { w: 4, color: RED });
      NB.write(String(k + 1), a + D / 2, y + 34, { font: 'hand', size: 34, weight: 700, color: RED, align: 'center', t0: ts + k * 0.35 + 0.2, dur: 0.2 });
    }
    const tb = ts + STICKS.whole * 0.35 + 0.2;
    if (T > tb) {
      NB.highlight(X0 + STICKS.whole * D, y - 16, STICKS.restLength, 32, { t0: tb, dur: 0.3 });
      NB.note('3 and a bit', X0 + STICKS.whole * D + STICKS.restLength / 2 + 70, y + 74, -0.03, { t0: tb + 0.3, size: 40 });
    }
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      NB.ground();
      rollScene(t0 + 0.1);
      // Jay rises in from below on the right, under the title
      const rise = E.outBack(P(T, t0 + 0.6, t0 + 1.1), 1.2);
      Puppet.draw('jay', 1790, 1080 + (1 - rise) * 600, 450, { facing: 0, t: T });
      // the title, handwritten; the highlighter lands on "Round"
      const { lines, size } = titleLines(L.title || 'Round and Round', 880, 130);
      let y = lines.length > 1 ? 250 : 320;
      lines.forEach((l, i) => {
        NB.title(l, 1400, y, { size, t0: t0 + 0.3 + i * 0.5, hi: i === 0 ? 'Round' : null });
        y += size * 1.2;
      });
      NB.write(L.subtitle || '', 1400, y + 20, { font: 'ui', size: 36, color: NB.PAL.pencil, align: 'center', t0: t0 + 1.2, dur: 0.4 });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 420, h: 136, size: 60, primary: true })) this.go();
      if (t > 2.4) NB.write(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 104 + (UI.tap(136) - 136) / 2, { font: 'ui', size: UI.text(30), color: NB.PAL.pencil, align: 'center', t0: t0 + 2.4 });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* the loading page (no assets yet): a pencil drawing a line along a ruler */
  Title.loading = function () { NB.loadingScreen('loading…'); };

  Levels.title = Title;
})();
